import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Label } from '@roger/ui-components';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  searchPatients,
  type PatientOption,
} from '../../services/faxClassification';
import {
  listIntakeDrafts,
  type IntakeDraft,
} from '../../services/intakeDrafts';
import { useStore } from '../../store';
import { draftSummary, patientLabel } from './patientOrDraftLabels';

/**
 * Who a fax is for, when the answer might not be a patient yet.
 *
 * A referral arrives before its patient exists: the patient is the result of
 * running an intake, so there is nothing to search for until somebody has.
 * That left the only honest answer to "whose is this" as "nobody", and the
 * pages sat unassigned while the rest of the packet was hunted down.
 *
 * So this searches two things. Patients, as before, and draft referrals, which
 * are just a name and the faxes gathered under it. When the name somebody
 * typed matches no open draft, starting one is offered as the last result,
 * which is how a draft comes to exist at all.
 *
 * A fax belongs to one or the other, never both. Choosing either clears the
 * other, because two answers to whose a fax is would leave a reader no rule
 * for which to believe.
 */

type Scope = 'both' | 'patients' | 'drafts';

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'both', label: 'Both' },
  { value: 'patients', label: 'Patients' },
  { value: 'drafts', label: 'Drafts' },
];

/**
 * What the field currently says.
 *
 * A label rather than the whole record, because the inbox row it is restored
 * from carries a name and an id and nothing else. Fabricating a patient out of
 * that to satisfy a type would invent a first and last name from one string.
 */
export interface PickerSelection {
  kind: 'patient' | 'draft';
  /**
   * Null for a draft the save is going to create.
   *
   * Starting one used to POST the moment it was clicked, which left a draft
   * behind with nothing in it whenever the fax was then closed without
   * saving: the draft persisted immediately, the fax-to-draft link only on
   * save. Now nothing is written until the save does both.
   */
  id: string | null;
  label: string;
  sublabel?: string;
}

export interface PatientOrDraftPickerProps {
  selected: PickerSelection | null;
  onChoosePatient: (patient: PatientOption) => void;
  onChooseDraft: (draft: IntakeDraft) => void;
  /** A draft to create on save, named but not yet written. */
  onStartDraft: (name: string) => void;
  onClear: () => void;
  disabled?: boolean;
  /**
   * Why a draft cannot be started yet, when it cannot. Shown on the option
   * and used to disable it, because a draft that cannot be saved with the fax
   * is a draft that would be left behind empty.
   */
  startDraftDisabledReason?: string | null;
  onError: (message: string) => void;
}

export default function PatientOrDraftPicker({
  selected,
  onChoosePatient,
  onChooseDraft,
  onStartDraft,
  onClear,
  disabled = false,
  startDraftDisabledReason = null,
  onError,
}: PatientOrDraftPickerProps) {
  // Draft search hits GET /intake-drafts, gated to intake:access. Without it
  // (Agency Admin / Scheduling at EMR-only agencies), every keystroke toast
  // "Could not search draft intakes." Hide that half instead.
  const canUseDrafts = useStore(state =>
    state.hasPermission(PermissionSlug.INTAKE_ACCESS)
  );
  const [scope, setScope] = useState<Scope>(canUseDrafts ? 'both' : 'patients');
  const [query, setQuery] = useState('');
  const [patientMatches, setPatientMatches] = useState<PatientOption[]>([]);
  const [draftMatches, setDraftMatches] = useState<IntakeDraft[]>([]);
  const [open, setOpen] = useState(false);

  const typed = query.trim();
  const showPatients = !canUseDrafts || scope !== 'drafts';
  const showDrafts = canUseDrafts && scope !== 'patients';

  useEffect(() => {
    if (!typed) {
      setPatientMatches([]);
      setDraftMatches([]);
      return;
    }
    // Debounced, because this is two queries per keystroke rather than a
    // filter over something already loaded.
    //
    // Clearing the timer only stops a search that has not started. A request
    // already in flight still resolves, so every result is gated on this flag:
    // without it a slow answer for an earlier query lands last and shows one
    // patient's match under another patient's name.
    let current = true;
    const timer = setTimeout(() => {
      void (async () => {
        if (showPatients) {
          try {
            const found = await searchPatients(typed);
            if (current) setPatientMatches(found);
          } catch {
            if (current) onError('Could not search patients.');
          }
        }
        if (showDrafts) {
          try {
            const found = await listIntakeDrafts(typed);
            if (current) setDraftMatches(found);
          } catch {
            if (current) onError('Could not search draft intakes.');
          }
        }
      })();
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [typed, showPatients, showDrafts, onError]);

  /**
   * Whether an open draft already goes by this name.
   *
   * Compared here as well as on the server so the option reads correctly
   * while typing. The server is still the one that decides: creating a name
   * that already exists returns the existing draft rather than a second one.
   */
  const nameIsTaken = useMemo(
    () =>
      draftMatches.some(
        match => match.name.toLowerCase() === typed.toLowerCase()
      ),
    [draftMatches, typed]
  );

  const startDraft = useCallback(() => {
    setQuery('');
    setPatientMatches([]);
    setDraftMatches([]);
    onStartDraft(typed);
  }, [typed, onStartDraft]);

  const choose = useCallback(
    (chosen: PatientOption | IntakeDraft, kind: 'patient' | 'draft') => {
      setQuery('');
      setPatientMatches([]);
      setDraftMatches([]);
      if (kind === 'patient') onChoosePatient(chosen as PatientOption);
      else onChooseDraft(chosen as IntakeDraft);
    },
    [onChoosePatient, onChooseDraft]
  );

  if (selected) {
    return (
      <div className='space-y-1.5'>
        <Label>{selected.kind === 'draft' ? 'Draft intake' : 'Patient'}</Label>
        <div className='flex items-center justify-between rounded border px-3 py-1.5'>
          <span className='min-w-0 text-sm'>
            <span className='block truncate'>{selected.label}</span>
            {selected.sublabel && (
              <span className='block text-xs text-muted-foreground'>
                {selected.sublabel}
              </span>
            )}
          </span>
          <Button
            variant='ghost'
            size='sm'
            disabled={disabled}
            onClick={onClear}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  }

  const hasResults =
    (showPatients && patientMatches.length > 0) ||
    (showDrafts && (draftMatches.length > 0 || typed.length > 0));

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between gap-2'>
        <Label htmlFor='fax-patient'>
          {canUseDrafts ? 'Patient or draft' : 'Patient'}
        </Label>
        {canUseDrafts && (
          <div
            className='inline-flex overflow-hidden rounded-md border'
            role='group'
            aria-label='What to search'
          >
            {SCOPES.map(option => (
              <button
                key={option.value}
                type='button'
                aria-pressed={scope === option.value}
                disabled={disabled}
                className={`px-2 py-0.5 text-xs ${
                  scope === option.value
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => setScope(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Floated over what is below rather than pushing it down, the way the
          type picker beside it does. */}
      <div className='relative'>
        <Input
          id='fax-patient'
          aria-label={
            canUseDrafts
              ? 'Search patients and draft intakes'
              : 'Search patients'
          }
          placeholder='Name or MRN'
          value={query}
          disabled={disabled}
          onChange={event => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />
        {open && hasResults && (
          <div
            className='absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-sm'
            // Keeps the input focused, so choosing something is a click rather
            // than a blur that closes the list first.
            onMouseDown={event => event.preventDefault()}
          >
            {showPatients && patientMatches.length > 0 && (
              <>
                <p className='px-2 py-1 text-xs uppercase tracking-wide text-muted-foreground'>
                  Patients
                </p>
                {patientMatches.map(match => (
                  <button
                    key={match.id}
                    type='button'
                    className='block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                    disabled={disabled}
                    onClick={() => choose(match, 'patient')}
                  >
                    {patientLabel(match)}
                  </button>
                ))}
              </>
            )}
            {showDrafts && (
              <>
                <p className='px-2 py-1 text-xs uppercase tracking-wide text-muted-foreground'>
                  Draft intakes
                </p>
                {draftMatches.map(match => (
                  <button
                    key={match.id}
                    type='button'
                    className='block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                    disabled={disabled}
                    onClick={() => choose(match, 'draft')}
                  >
                    <span className='block'>{match.name}</span>
                    <span className='block text-xs text-muted-foreground'>
                      {draftSummary(match)}
                    </span>
                  </button>
                ))}
                {/* Last, and only when no open draft already answers to this
                    name: offering to start a second one for the same referral
                    is how an office ends up with half a packet in each. */}
                {typed.length > 0 && !nameIsTaken && (
                  <button
                    type='button'
                    className='block w-full rounded px-2 py-1.5 text-left text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent'
                    disabled={disabled || !!startDraftDisabledReason}
                    onClick={startDraft}
                  >
                    <span className='block'>
                      Start draft intake for &ldquo;{typed}&rdquo;
                    </span>
                    <span className='block text-xs font-normal text-muted-foreground'>
                      {startDraftDisabledReason ??
                        'Collects faxes until you are ready to run the intake'}
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
